import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import './SmartBase.css';
import { NewZoneModal, EditZoneModal, CreateGroupModal, EditGroupModal, CreateValueModal } from './components/Modals';
import RecommendationsBlock from './components/RecommendationsBlock';
import PicturesBlock from './components/PicturesBlock';
import PatientInfoBlock from './components/PatientInfoBlock';
import { renderTablePlaceholders, serializeTableField } from './components/TablePlaceholderEditor';
import authFetch from '../../utils/authFetch';
import { handleUndo as handleUndoUtil, handleRedo as handleRedoUtil } from '../../utils/TemplateUtils';
import { resetDraftBlocks } from '../../features/template-workspace/draft/model/draftBlocks';
import { useNormaPrefill } from '../../features/template-workspace/norma-prefill/useNormaPrefill';
import { useWordExport } from '../../features/template-workspace/word-export/useWordExport';
import WordExportButton from '../../features/template-workspace/word-export/WordExportButton';
import WordExportProgressOverlay from '../../features/template-workspace/word-export/WordExportProgressOverlay';
import AiPromptLibrary from '../../features/template-workspace/ai-prompts/AiPromptLibrary';
import { getTemplateAlias } from '../HomePage';

const LOCAL_STORAGE_KEY = 'smart_template_draft_';
const LEGACY_IMAGES_STORAGE_KEY_PREFIX = 'smart_template_images_';
const DEFAULT_RECOMMENDATIONS_TEXT = 'Консультация лечащего врача.';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const initializeImagesSession = () => {
  // Старые версии сохраняли Base64-снимки в localStorage и могли исчерпать
  // его квоту. Очистка выполняется до первой записи остальных данных страницы.
  Object.keys(localStorage)
    .filter(key => key.startsWith(LEGACY_IMAGES_STORAGE_KEY_PREFIX))
    .forEach(key => localStorage.removeItem(key));

  return [];
};

/**
 * Компонент страницы шаблона
 * Отвечает за отображение и управление смарт-шаблоном
 */
const TemplatePage = () => {
  // ================ Состояния ================
  // Параметры маршрутизации
  const { templateId } = useParams();
  const location = useLocation();
  const smartTemplateDescriptionFromState = location.state?.smartTemplateDescription || '';
  const navigate = useNavigate();

  // Основные состояния
  const [templateDescription, setTemplateDescription] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [openZone, setOpenZone] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Состояние пользователя
  const [aiAccess, setAiAccess] = useState(false);

  // Состояния модальных окон
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalParentGroup, setModalParentGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');

  const [isPlusModalOpen, setIsPlusModalOpen] = useState(false);
  const [modalPlusGroup, setModalPlusGroup] = useState(null);
  const [newPlusGroupName, setNewPlusGroupName] = useState('');
  const [newPlusValue, setNewPlusValue] = useState('');
  const [newPlusReportValue, setNewPlusReportValue] = useState('');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupValue, setEditGroupValue] = useState('');
  const [editGroupReportValue, setEditGroupReportValue] = useState('');

  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);

  const [isNewZoneModalOpen, setIsNewZoneModalOpen] = useState(false);
  const [newZoneDescription, setNewZoneDescription] = useState('');
  const [generatedZoneSystemName, setGeneratedZoneSystemName] = useState('');
  const [isSavingZone, setIsSavingZone] = useState(false);

  // Снимки и подписи существуют только в состоянии открытой страницы.
  const [images, setImages] = useState(initializeImagesSession);

  const [recommendationsText, setRecommendationsText] = useState(() => {
    const saved = localStorage.getItem('smart_template_recommendations_' + templateId);
    return saved || DEFAULT_RECOMMENDATIONS_TEXT;
  });

  const [patientName, setPatientName] = useState(() => {
    const saved = localStorage.getItem('smart_template_patient_name_' + templateId);
    return saved || '';
  });
  const [patientBirthdate, setPatientBirthdate] = useState(() => {
    const saved = localStorage.getItem('smart_template_patient_birthdate_' + templateId);
    return saved || '';
  });
  const [patientGender, setPatientGender] = useState(() => {
    const saved = localStorage.getItem('smart_template_patient_gender_' + templateId);
    return saved || '';
  });

  // Состояния для новой строки исследования
  const [studyNumber, setStudyNumber] = useState(() => {
    const saved = localStorage.getItem('smart_template_study_number_' + templateId);
    return saved || '';
  });
  const [studyDate, setStudyDate] = useState(() => {
    const saved = localStorage.getItem('smart_template_study_date_' + templateId);
    return saved || '';
  });
  const [studyType, setStudyType] = useState(() => {
    const saved = localStorage.getItem('smart_template_study_type_' + templateId);
    return saved || 'Магнитно-резонансная томография';
  });
  const [contrastType, setContrastType] = useState(() => {
    const saved = localStorage.getItem('smart_template_contrast_type_' + templateId);
    return saved || '';
  });
  const [studyNote, setStudyNote] = useState(() => {
    const saved = localStorage.getItem('smart_template_study_note_' + templateId);
    return saved || '';
  });

  // Состояние для жалоб и анамнеза
  const [complaints, setComplaints] = useState(() => {
    const saved = localStorage.getItem('smart_template_complaints_' + templateId);
    return saved || '';
  });

  // Состояния для модалки редактирования описания зоны
  const [isEditZoneModalOpen, setIsEditZoneModalOpen] = useState(false);
  const [editZoneDescription, setEditZoneDescription] = useState('');
  const [editZoneName, setEditZoneName] = useState('');

  // Состояние для отслеживания активной зоны (в фокусе)
  const [activeZoneName, setActiveZoneName] = useState(null);

  // --- Новый useRef для caret ---
  const caretPositionRef = useRef({ zoneName: null, instanceId: null, pos: null });

  // Добавить состояние для чекбокса "Норма" в EditGroupModal
  const [isEditNormal, setIsEditNormal] = useState(false);

  const [isPlusNormal, setIsPlusNormal] = useState(false);

  // Функция для сохранения текущих значений всех плейсхолдеров
  const saveAllPlaceholderValues = () => {
    const allBlocks = document.querySelectorAll('.editable-content');
    allBlocks.forEach(block => {
      const zoneName = block.dataset.zone;
      const instanceId = block.dataset.instance;
      savePlaceholderValues(zoneName, instanceId, block);
    });
  };

  // Функция для восстановления значений всех плейсхолдеров
  const restoreAllPlaceholderValues = () => {
    const allBlocks = document.querySelectorAll('.editable-content');
    allBlocks.forEach(block => {
      const zoneName = block.dataset.zone;
      const instanceId = block.dataset.instance;
      restorePlaceholderValues(zoneName, instanceId, block);
    });
  };

  // Функция для проверки на пробел перед запятой или точкой
  const checkMultipleSpaces = (text) => {    
    if (/\s[,.]/.test(text)) return true;    
    return false;
  };

  // Функция для формирования заключения (объединяет логику "Подготовить" и "Сформировать")
  const handleFormConclusion = (zoneName, instanceId) => {
    // Сначала выполняем логику "Подготовить"
    prepareConclusion(zoneName, instanceId);
    // Затем выполняем логику "Сформировать"
    applyPlaceholders(zoneName, instanceId);

    // Проверяем все зоны на пробел перед запятой или точкой
    const allBlocks = document.querySelectorAll('.editable-content');
    let foundMultipleSpaces = false;
    
    allBlocks.forEach(block => {
      const text = block.textContent || block.innerText;
      if (checkMultipleSpaces(text)) {
        block.style.backgroundColor = '#ffebee'; // Светло-красный фон
        block.style.border = '1px solid #ffcdd2'; // Красная рамка
        foundMultipleSpaces = true;
      } else {
        block.style.backgroundColor = ''; // Возвращаем исходный фон
        block.style.border = ''; // Возвращаем исходную рамку
      }
    });

    // Если множественных пробелов не найдено, восстанавливаем исходный цвет всех зон
    if (!foundMultipleSpaces) {
      allBlocks.forEach(block => {
        block.style.backgroundColor = '';
        block.style.border = '';
      });
    }
  };

  // Функция для применения плейсхолдеров во всех зонах (кроме Заключения)
  const applyAllPlaceholders = () => {
    blocks.forEach(block => {
      // Пропускаем блок Заключения
      if (block.zone_name === `${templateId}_conclusion`) return;
      
      block.instances.forEach(inst => {
        applyPlaceholders(block.zone_name, inst.id);
      });
    });
  };

  // Функция для сохранения значений плейсхолдеров
  const savePlaceholderValues = (zoneName, instanceId, element) => {
    const placeholderValues = {};
    const inputs = element.querySelectorAll('.inline-input');
    const selects = element.querySelectorAll('.inline-select');
    
    inputs.forEach(input => {
      if (input.value) {
        const fieldId = input.dataset.fieldId;
        const syncId = input.dataset.syncId;
        // Используем syncId если есть, иначе fieldId
        const key = syncId || fieldId;
        placeholderValues[key] = input.value;
      }
    });
    
    selects.forEach(select => {
      if (select.value) {
        const fieldId = select.dataset.fieldId;
        const syncId = select.dataset.syncId;
        // Используем syncId если есть, иначе fieldId
        const key = syncId || fieldId;
        placeholderValues[key] = select.value;
      }
    });

    if (Object.keys(placeholderValues).length > 0) {
      // Сохраняем значения только для текущей зоны
      const key = `smart_template_placeholders_${templateId}_${zoneName}_${instanceId}`;
      localStorage.setItem(key, JSON.stringify(placeholderValues));
    }
  };

  // Функция для восстановления значений плейсхолдеров
  const restorePlaceholderValues = (zoneName, instanceId, element) => {
    const key = `smart_template_placeholders_${templateId}_${zoneName}_${instanceId}`;
    const savedValues = localStorage.getItem(key);
    
    if (savedValues) {
      const placeholderValues = JSON.parse(savedValues);
      
      // Восстанавливаем значения для всех плейсхолдеров в текущей зоне
      Object.entries(placeholderValues).forEach(([fieldId, value]) => {
        const inputs = element.querySelectorAll(`[data-field-id="${fieldId}"], [data-sync-id="${fieldId}"]`);
        inputs.forEach(input => {
          input.value = value;
        });
      });
    }
  };

  // Функция для синхронизации значений плейсхолдеров между всеми зонами
  const syncPlaceholderValues = (value, fieldId, syncId) => {
    // Получаем все блоки, кроме текущего
    const allBlocks = document.querySelectorAll('.editable-content');
    allBlocks.forEach(block => {
      const inputs = block.querySelectorAll(`[data-field-id="${fieldId}"], [data-sync-id="${syncId}"]`);
      inputs.forEach(input => {
        input.value = value;
        // Сохраняем обновленное значение для этой зоны
        const zoneName = block.dataset.zone;
        const instanceId = block.dataset.instance;
        savePlaceholderValues(zoneName, instanceId, block);
      });
    });
  };

  // Обновляем useEffect для обработки изменений в плейсхолдерах
  useEffect(() => {
    const handlePlaceholderChange = (e) => {
      if (e.target.classList.contains('inline-input') || e.target.classList.contains('inline-select')) {
        const fieldId = e.target.dataset.fieldId;
        const syncId = e.target.dataset.syncId;
        const value = e.target.value;
        
        // Сохраняем значение для текущей зоны
        const element = e.target.closest('.editable-content');
        if (element) {
          const zoneName = element.dataset.zone;
          const instanceId = element.dataset.instance;
          savePlaceholderValues(zoneName, instanceId, element);
        }
        
        // Синхронизируем значение со всеми зонами
        syncPlaceholderValues(value, fieldId, syncId);
      }
    };

    document.addEventListener('input', handlePlaceholderChange);
    return () => document.removeEventListener('input', handlePlaceholderChange);
  }, [blocks]);

  // Обновляем useEffect для восстановления значений при загрузке страницы
  useEffect(() => {
    // Восстанавливаем значения для всех зон
    blocks.forEach(block => {
      block.instances.forEach(inst => {
        const element = document.querySelector(`[data-zone="${block.zone_name}"][data-instance="${inst.id}"]`);
        if (element) {
          restorePlaceholderValues(block.zone_name, inst.id, element);
        }
      });
    });
  }, [blocks]);

  useEffect(() => {
    localStorage.setItem('smart_template_recommendations_' + templateId, recommendationsText);
  }, [recommendationsText, templateId]);

  useEffect(() => {
    localStorage.setItem('smart_template_patient_name_' + templateId, patientName);
  }, [patientName, templateId]);

  useEffect(() => {
    localStorage.setItem('smart_template_patient_birthdate_' + templateId, patientBirthdate);
  }, [patientBirthdate, templateId]);

  useEffect(() => {
    localStorage.setItem('smart_template_patient_gender_' + templateId, patientGender);
  }, [patientGender, templateId]);

  useEffect(() => {
    localStorage.setItem('smart_template_study_number_' + templateId, studyNumber);
  }, [studyNumber, templateId]);
  useEffect(() => {
    localStorage.setItem('smart_template_study_date_' + templateId, studyDate);
  }, [studyDate, templateId]);
  useEffect(() => {
    localStorage.setItem('smart_template_study_type_' + templateId, studyType);
  }, [studyType, templateId]);
  useEffect(() => {
    localStorage.setItem('smart_template_contrast_type_' + templateId, contrastType);
  }, [contrastType, templateId]);
  useEffect(() => {
    localStorage.setItem('smart_template_study_note_' + templateId, studyNote);
  }, [studyNote, templateId]);

  useEffect(() => {
    localStorage.setItem('smart_template_complaints_' + templateId, complaints);
  }, [complaints, templateId]);

  // Синхронизировать состояние чекбокса при открытии модалки редактирования
  useEffect(() => {
    if (isEditModalOpen && editGroup && editGroup.group_value !== null && editGroup.group_value !== '') {
      setIsEditNormal(editGroup.normal === 1);
    }
  }, [isEditModalOpen, editGroup]);

  useEffect(() => {
    if (isPlusModalOpen) {
      setIsPlusNormal(false);
    }
  }, [isPlusModalOpen]);

  // Добавляем новый useEffect для сохранения порядка элементов
  useEffect(() => {
    const saveGroupsOrder = () => {
      blocks.forEach(block => {
        const groups = block.groups.sort((a, b) => {
          // Сначала проверяем root = 1
          if (a.root === 1 && b.root !== 1) return -1;
          if (a.root !== 1 && b.root === 1) return 1;
          // Затем группируем по наличию group_value
          if (a.group_value && !b.group_value) return -1;
          if (!a.group_value && b.group_value) return 1;
          // Затем сортируем по group_id внутри каждой группы
          return a.group_id - b.group_id;
        });
        block.groups = groups;
      });
    };

    saveGroupsOrder();
  }, [blocks]);

  // ================ API и данные ================
  /**
   * Загрузка данных шаблона с сервера
   * Группирует данные по зонам и создает структуру блоков
   */
  const fetchData = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/templates/?smart_template_name=${templateId}`);
      const data = await res.json();

      const grouped = {};
      if (data.length > 0) {
  setTemplateDescription(data[0].smart_template_description || templateId);
}
      
      // Группировка данных по зонам
      data.forEach(item => {
        const zone = item.zone_name;
        if (!grouped[zone]) {
          grouped[zone] = {
            zone_description: item.zone_description,
            groups: new Map()
          };
        }
        if (item.parent_group_id === null || item.parent_group_id === undefined) {
          grouped[zone].groups.set(item.group_id, {
            group_id: item.group_id,
            group_description: item.group_description,
            group_value: item.group_value || "",
            report_value: item.report_value || "",
            parent_group_id: null,
            root: item.root || 0,
            smart_template_name: item.smart_template_name,
            smart_template_description: item.smart_template_description,
            zone_name: item.zone_name,
            zone_description: item.zone_description,
            zone_index: item.zone_index,
            normal: item.normal
          });
        }
      });

      // Загрузка черновика для конкретного шаблона
      const draft = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY + templateId) || '{}');
      const blocksArray = Object.entries(grouped).map(([zone, { zone_description, groups }]) => ({
        zone_name: zone,
        zone_description,
        groups: Array.from(groups.values()),
        instances: (draft[zone]?.map(inst => ({
          ...inst,
          history: inst.history || [inst.text || ''],
          historyIndex: inst.historyIndex || 0
        })) || [{ id: Date.now(), text: '', history: [''], historyIndex: 0 }])
      }));

      // Добавляем блок Заключение
      blocksArray.push({
        zone_name: `${templateId}_conclusion`,
        zone_description: 'Заключение',
        groups: [],
        instances: (draft[`${templateId}_conclusion`]?.map(inst => ({
          ...inst,
          history: inst.history || [inst.text || ''],
          historyIndex: inst.historyIndex || 0
        })) || [{ id: Date.now(), text: '', history: [''], historyIndex: 0 }])
      });

      // === Сортировка блоков (зон) по zone_index ===
      // Отдельно выделяем блок заключения
      const conclusionBlock = blocksArray.find(b => b.zone_name === `${templateId}_conclusion`);
      const otherBlocks = blocksArray.filter(b => b.zone_name !== `${templateId}_conclusion`);
      // Сортируем остальные блоки по zone_index корневой группы (root === 1)
      otherBlocks.sort((a, b) => {
        const aRoot = a.groups.find(g => g.root === 1);
        const bRoot = b.groups.find(g => g.root === 1);
        const aZoneIndex = aRoot?.zone_index;
        const bZoneIndex = bRoot?.zone_index;
        
        // Если zone_index не задан, используем старую логику по group_id
        if (aZoneIndex === null || aZoneIndex === undefined) {
          return (aRoot?.group_id || 0) - (bRoot?.group_id || 0);
        }
        if (bZoneIndex === null || bZoneIndex === undefined) {
          return (aRoot?.group_id || 0) - (bRoot?.group_id || 0);
        }
        
        // Сортируем по zone_index
        return aZoneIndex - bZoneIndex;
      });
      // Собираем итоговый массив: сначала зоны, потом заключение
      const sortedBlocks = [...otherBlocks, conclusionBlock];
      setBlocks(sortedBlocks);
    } catch (error) {
      console.error("Ошибка загрузки шаблонов:", error);
    }
  }, [templateId]);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Функция для получения информации о пользователе
  const fetchUserInfo = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/user/`);
      if (res.ok) {
        const data = await res.json();
        setAiAccess(data.ai_access === 1);
      }
    } catch (error) {
      console.error("Ошибка загрузки информации о пользователе:", error);
    }
  }, []);

  // Загрузка информации о пользователе при монтировании компонента
  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  // ================ Работа с черновиком ================
  /**
   * Сохранение черновика в localStorage
   * Срабатывает при каждом изменении блоков
   */
useEffect(() => {
    if (blocks.length === 0) return;

  const draftData = {};
  blocks.forEach(block => {
    draftData[block.zone_name] = block.instances.map(inst => ({
      id: inst.id,
      text: inst.text,
      history: inst.history || [inst.text || ''],
      historyIndex: inst.historyIndex || 0
    }));
  });

    localStorage.setItem(LOCAL_STORAGE_KEY + templateId, JSON.stringify(draftData));
  }, [blocks, templateId]);

  /**
   * Очистка черновика
   * Удаляет все сохраненные данные из localStorage
   */
  const clearDraft = () => {
    if (window.confirm('Очистить черновик текущего шаблона?')) {
      localStorage.removeItem(LOCAL_STORAGE_KEY + templateId);
      setBlocks(previousBlocks => resetDraftBlocks(previousBlocks));
      // Очищаем значения плейсхолдеров для всех зон
      blocks.forEach(block => {
        block.instances.forEach(inst => {
          const key = `smart_template_placeholders_${templateId}_${block.zone_name}_${inst.id}`;
          localStorage.removeItem(key);
        });
      });
      // Очищаем картинки и подписи
      setImages([]);
      setRecommendationsText(DEFAULT_RECOMMENDATIONS_TEXT);
      localStorage.setItem(
        'smart_template_recommendations_' + templateId,
        DEFAULT_RECOMMENDATIONS_TEXT
      );
      setPatientName('');
      setPatientBirthdate('');
      setPatientGender('');
      localStorage.removeItem('smart_template_patient_name_' + templateId);
      localStorage.removeItem('smart_template_patient_birthdate_' + templateId);
      localStorage.removeItem('smart_template_patient_gender_' + templateId);
      setStudyNumber('');
      setStudyType('Магнитно-резонансная томография');
      setContrastType('');
      setStudyNote('');
      setComplaints('');
      localStorage.removeItem('smart_template_study_number_' + templateId);
      localStorage.removeItem('smart_template_study_type_' + templateId);
      localStorage.removeItem('smart_template_contrast_type_' + templateId);
      localStorage.removeItem('smart_template_study_note_' + templateId);
      localStorage.removeItem('smart_template_complaints_' + templateId);
      setStudyDate('');
      localStorage.removeItem('smart_template_study_date_' + templateId);

      // Восстанавливаем исходный цвет всех зон
      const allBlocks = document.querySelectorAll('.editable-content');
      allBlocks.forEach(block => {
        block.style.backgroundColor = '';
        block.style.border = '';
      });
    }
  };

  // ================ Работа с группами ================
  /**
   * Создание новой группы с описанием
   */
  const handleCreatePlusGroup = async () => {
  if (!newPlusGroupName.trim() || !newPlusValue.trim()) {
    alert('Название и значение обязательны');
    return;
  }

  // Удалена логика автоматического определения normal по названию группы
  const normal = isPlusNormal ? 1 : 0;

  try {
    const payload = {
      parent_group_id: modalPlusGroup.root === 1 ? null : modalPlusGroup.group_id,
      group_description: newPlusGroupName.trim(),
      group_value: newPlusValue.trim(),
      report_value: newPlusReportValue.trim() || undefined,
      normal
    };

    if (modalPlusGroup.root === 1) {
      payload.smart_template_name = modalPlusGroup.smart_template_name;
      payload.smart_template_description = modalPlusGroup.smart_template_description;
      payload.zone_name = modalPlusGroup.zone_name;
      payload.zone_description = modalPlusGroup.zone_description;
    }

    const res = await authFetch(`${API_URL}/create-group/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await res.json();

    if (result.success) {
      setIsPlusModalOpen(false);
      setNewPlusValue('');
      setNewPlusGroupName('');
      setNewPlusReportValue('');
      await fetchData();
      setExpandedGroups({});
    } else {
      alert(result.error || 'Ошибка создания');
    }
  } catch (err) {
    console.error('Ошибка при создании (➕):', err);
    alert('Ошибка сети');
  }
};

  /**
   * Создание новой группы
   */
  const handleCreateGroup = async () => {
  if (!newGroupName.trim()) {
    alert('Название группы обязательно');
    return;
  }

  try {
    // Добавляем символ 🔽 к названию, если это папка
    const groupDescription = newGroupName.trim() + ' 🔽';

    const payload = {
      parent_group_id: modalParentGroup.root === 1 ? null : modalParentGroup.group_id,
      group_description: groupDescription
    };

    if (modalParentGroup.root === 1) {
      payload.smart_template_name = modalParentGroup.smart_template_name;
      payload.smart_template_description = modalParentGroup.smart_template_description;
      payload.zone_name = modalParentGroup.zone_name;
      payload.zone_description = modalParentGroup.zone_description;
    }

    const res = await authFetch(`${API_URL}/create-group/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await res.json();

    if (result.success) {
      setIsModalOpen(false);
      setNewGroupName('');
      await fetchData();
        setExpandedGroups({});
    } else {
      alert(result.error || 'Ошибка добавления');
    }
  } catch (error) {
    console.error('Ошибка при создании (📁):', error);
    alert('Ошибка сети');
  }
};

  /**
   * Переключение отображения дочерних групп
   */
  const toggleChildGroups = async (groupId) => {
    // Сохраняем позицию прокрутки библиотеки заключений
    const libraryContainer = document.querySelector('.tool-list.show');
    const scrollTop = libraryContainer ? libraryContainer.scrollTop : 0;
    
    // Сохраняем значения перед открытием/закрытием подгрупп
    saveAllPlaceholderValues();
    
    if (expandedGroups[groupId]) {
      setExpandedGroups(prev => {
        const updated = { ...prev };
        delete updated[groupId];
        return updated;
      });
    } else {
      try {
        const res = await authFetch(`${API_URL}/child-groups/?group_id=${groupId}`);
        const data = await res.json();
        // Сортируем полученные данные
        const sortedData = data.sort((a, b) => {
          // Сначала группируем по наличию group_value
          if (a.group_value && !b.group_value) return -1;
          if (!a.group_value && b.group_value) return 1;
          // Затем сортируем по group_id внутри каждой группы
          return a.group_id - b.group_id;
        });
        setExpandedGroups(prev => ({
          ...prev,
          [groupId]: sortedData
        }));
      } catch (error) {
        console.error("Ошибка загрузки подгрупп:", error);
      }
    }
    
    // Восстанавливаем значения после обновления состояния
    // Используем requestAnimationFrame для гарантии, что DOM обновился
    requestAnimationFrame(() => {
      restoreAllPlaceholderValues();
      // Восстанавливаем позицию прокрутки
      if (libraryContainer) {
        libraryContainer.scrollTop = scrollTop;
      }
    });
  };

  // ================ Работа с текстом ================
  /**
   * Обновление текста в блоке
   */
  const updateText = (zoneName, instanceId, newText) => {
    setBlocks(prev =>
      prev.map(block =>
        block.zone_name === zoneName
          ? {
              ...block,
              instances: block.instances.map(inst => {
                if (inst.id === instanceId) {
                  // Если текст не изменился, не обновляем историю
                  if (inst.text === newText) return inst;
                  // Если был откат (undo), и пользователь начал вводить новый текст — обрезаем историю вперед
                  let newHistory = inst.history.slice(0, inst.historyIndex + 1);
                  newHistory.push(newText);
                  if (newHistory.length > 20) newHistory = newHistory.slice(newHistory.length - 20);
                  return {
                    ...inst,
                    text: newText,
                    history: newHistory,
                    historyIndex: newHistory.length - 1
                  };
                }
                return inst;
              })
            }
          : block
      )
    );
  };

  /**
   * Обработка изменений в инлайн-полях
   */
  const handleContentChange = (zoneName, instanceId, event) => {
    const element = event.target;
    
    // Если изменение произошло в инлайн-поле или select, игнорируем
    if (event.target.classList.contains('inline-input') || event.target.classList.contains('inline-select')) {
      return;
    }

    // --- сохраняем caret в ref ---
    caretPositionRef.current = {
      zoneName,
      instanceId,
      pos: saveCaretPosition(element)
    };
    let content = element.innerHTML;
    // Обновляем только если текст реально изменился
    const block = blocks.find(b => b.zone_name === zoneName);
    const inst = block?.instances.find(inst => inst.id === instanceId);
    if (inst && inst.text !== content) {
      updateText(zoneName, instanceId, content);
      // Сохраняем значения плейсхолдеров
      savePlaceholderValues(zoneName, instanceId, element);
    }
  };

  /**
   * Вставка текста в блок с обработкой плейсхолдеров
   */
  const handleInsertText = (zoneName, instanceId, groupId) => {
    // Сохраняем позицию прокрутки библиотеки заключений до обновления состояния,
    // иначе при ререндере .editable-content (через dangerouslySetInnerHTML)
    // браузер автоматически скроллит страницу/контейнер к каретке.
    const libraryContainer = document.querySelector('.tool-list.show');
    const savedScrollTop = libraryContainer ? libraryContainer.scrollTop : 0;

    // Рекурсивный поиск группы по group_id с учётом вложенности
    function findGroupById(groups) {
      for (const g of groups) {
        if (g.group_id === groupId) return g;
        if (expandedGroups[g.group_id]) {
          const found = findGroupById(expandedGroups[g.group_id]);
          if (found) return found;
        }
      }
      return null;
    }
    const block = blocks.find(b => b.zone_name === zoneName);
    let group = null;
    if (block && block.groups) {
      group = findGroupById(block.groups);
    }
    if (!group) {
      return;
    }

    // Восстанавливаем скролл библиотеки после всех ререндеров и возможных автоскроллов браузера.
    // Двойной requestAnimationFrame гарантирует, что мы выполнимся после обновления DOM
    // и после восстановления каретки в useEffect([blocks]).
    const restoreScroll = () => {
      const container = document.querySelector('.tool-list.show');
      if (container) {
        container.scrollTop = savedScrollTop;
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
    });

    // Дальнейшая логика — как раньше, но через общий пайплайн обработки плейсхолдеров.
    let processedText = processPlaceholders(group.group_value);

    // Обновляем текущую зону
    setBlocks(prev =>
      prev.map(block =>
        block.zone_name === zoneName
          ? {
              ...block,
              instances: block.instances.map(inst => {
                const isEmpty = !inst.text || inst.text.replace(/<br\s*\/?\>/gi, '').trim() === '';
                return inst.id === instanceId
                  ? {
                      ...inst,
                      text: isEmpty
                        ? processedText
                        : inst.text + (inst.text.endsWith('<br>') ? '' : '<br>') + processedText
                  }
                : inst;
              })
            }
          : block
      )
    );

    // Если есть report_value — вставляем в заключение
    if (group?.report_value) {
      let processedReportValue = group.report_value.replace(
        /\(([^)]+)\)\/\/\(([^)]+)\)%([^%]*)?%/g,
        (match, firstValue, secondValue, prefilledValue = '') => {
          const conclusionBlock = blocks.find(b => b.zone_name === `${templateId}_conclusion`);
          if (!conclusionBlock) return match;
          const currentText = conclusionBlock.instances[0].text;
          const existingWithFirstValue = currentText.includes(`value="${firstValue}"`);
          const existingWithSecondValue = currentText.includes(`value="${secondValue}"`);
          if (existingWithFirstValue) {
            const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newField = `<span class="inline-field" contenteditable="false"><input type="text" class="inline-input" data-field-id="${fieldId}" value="${secondValue}" /></span>`;
            setBlocks(prev =>
              prev.map(block =>
                block.zone_name === `${templateId}_conclusion`
                  ? {
                      ...block,
                      instances: block.instances.map(inst => ({
                        ...inst,
                        text: inst.text.replace(
                          new RegExp(
                            `<span class=\"inline-field\" contenteditable=\"false\"><input type=\"text\" class=\"inline-input\" data-field-id=\"[^\"]+\" value=\"${firstValue}\" \/><\/span>` +
                            `(?:<span class=\"inline-field\" contenteditable=\"false\"><input type=\"text\" class=\"inline-input\" data-field-id=\"[^\"]+\" value=\"[^\"]*\" readonly \/><\/span>)?`,
                            'g'
                          ),
                          newField
                        )
                      }))
                    }
                  : block
              )
            );
            return '';
          } else if (!existingWithSecondValue) {
            const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            return `<span class="inline-field" contenteditable="false"><input type="text" class="inline-input" data-field-id="${fieldId}" value="${firstValue}" /></span>`;
          } else {
            return '';
          }
        }
      );
      if (processedReportValue && group.report_value.match(/%([^%]*)?%/)) {
        const prefilledValue = group.report_value.match(/%([^%]*)?%/)[1] || '';
        const emptyFieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        processedReportValue = processedReportValue.trim() + ' <span class="inline-field" contenteditable="false"><input type="text" class="inline-input" data-field-id="' + emptyFieldId + '" value="' + prefilledValue + '" readonly /></span>';
      }
      if (processedReportValue.trim()) {
        const processedWithPlaceholders = processPlaceholders(processedReportValue);
        setBlocks(prev =>
          prev.map(block =>
            block.zone_name === `${templateId}_conclusion`
              ? {
                  ...block,
                  instances: block.instances.map(inst => {
                    const isEmpty = !inst.text || inst.text.replace(/<br\s*\/?\>/gi, '').trim() === '';
                    return {
                      ...inst,
                      text: isEmpty
                        ? processedWithPlaceholders
                        : inst.text + (inst.text.endsWith('<br>') ? '' : '<br>') + processedWithPlaceholders
                    };
                  })
                }
              : block
          )
        );
      }
    }
  };

  /**
   * Очистка текста в блоке
   */
  const clearText = (zoneName, instanceId) => {
    updateText(zoneName, instanceId, '');
  };

  /**
   * Копирование экземпляра блока
   */
  const copyInstance = (zoneName) => {
    setBlocks(prev =>
      prev.map(block =>
        block.zone_name === zoneName
          ? {
              ...block,
              instances: [
                ...block.instances,
                {
                  id: Date.now(),
                  text: '',
                  history: [''],
                  historyIndex: 0
                }
              ]
            }
          : block
      )
    );
  };

  /**
   * Удаление экземпляра блока
   */
  const removeInstance = (zoneName, instanceId) => {
    setBlocks(prev =>
      prev.map(block =>
        block.zone_name === zoneName
          ? {
              ...block,
              instances:
                block.instances.length > 1
                  ? block.instances.filter(inst => inst.id !== instanceId)
                  : block.instances
            }
          : block
      )
    );
  };

  /**
   * Переключение отображения зоны
   */
  const toggleZone = (zoneName, instanceId) => {
    // Сохраняем значения перед открытием/закрытием библиотеки
    saveAllPlaceholderValues();
    
    const key = `${zoneName}-${instanceId}`;
    setOpenZone(prev => (prev === key ? null : key));
    
    // Восстанавливаем значения после обновления состояния
    setTimeout(() => {
      restoreAllPlaceholderValues();
    }, 0);
  };

  /**
   * Сохранение и восстановление позиции каретки
   */
  const saveCaretPosition = (element) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  };

  const restoreCaretPosition = (element, pos) => {
    const range = document.createRange();
    const selection = window.getSelection();
    
    let charIndex = 0;
    let foundStart = false;
    
    const traverseNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCharIndex = charIndex + node.length;
        if (!foundStart && pos <= nextCharIndex) {
          range.setStart(node, pos - charIndex);
          range.setEnd(node, pos - charIndex);
          foundStart = true;
          return true;
        }
        charIndex = nextCharIndex;
  } else {
        for (const childNode of node.childNodes) {
          if (traverseNodes(childNode)) {
            return true;
          }
        }
      }
      return false;
    };
    
    traverseNodes(element);
    
    if (foundStart) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Закрываем попап при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setActivePopup(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Обработчик клавиши Escape для закрытия библиотеки заключений
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && openZone) {
        setOpenZone(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openZone]);

  /**
   * Обработка клика по плейсхолдеру
   */
  const handlePlaceholderClick = (e) => {
    const target = e.target;
    if (target.classList.contains('placeholder')) {
      e.preventDefault();
      e.stopPropagation();

      const rect = target.getBoundingClientRect();
      const placeholder = target.textContent;
      const match = placeholder.match(/\(\(([^)]+)\)\)\{\{([^}]+)\}\}/);
      
      if (match) {
        const [, name, values] = match;
        setActivePopup({
          placeholder: name,
          values: values.split(';').map(v => v.trim()),
          position: {
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX
          }
        });
      }
    }
  };

  /**
   * Обработка выбора значения из выпадающего списка
   */
  const handleValueSelect = (zoneName, instanceId, placeholder, value) => {
    const element = document.querySelector(`[data-zone="${zoneName}"][data-instance="${instanceId}"]`);
    if (element) {
      const content = element.innerHTML.replace(
        new RegExp(`<span class="placeholder">${placeholder}</span>`),
        value
      );
      updateText(zoneName, instanceId, content);
    }
    setActivePopup(null);
  };

  /**
   * Обработка вставки текста
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  /**
   * Применение значений плейсхолдеров
   */
  const applyPlaceholders = (zoneName, instanceId) => {
    const element = document.querySelector(`[data-zone="${zoneName}"][data-instance="${instanceId}"]`);
    if (!element) return;

    // Обработка всех инлайн-полей (и текстовых, и выпадающих списков)
    const inlineFields = element.querySelectorAll('.inline-field');
    inlineFields.forEach(field => {
      const tableValue = serializeTableField(field);
      if (tableValue !== null) {
        field.outerHTML = tableValue;
        return;
      }

      const input = field.querySelector('.inline-input');
      const select = field.querySelector('.inline-select');
      
      if (input) {
        // Для текстовых полей
        let value = input.value || '';
        // Удаляем конструкцию prefix- и её значение
        value = value.replace(/prefix-[^\s,]+\s*/g, '');
        // Удаляем конструкцию key- если она есть
        value = value.replace(/key-[^\s,]+\s*/g, '').trim();
        field.outerHTML = value;
      } else if (select) {
        // Для выпадающих списков
        let value = select.value;
        if (!value) {
          // Если value пустой, ищем выбранный option
          const selectedOption = select.options[select.selectedIndex];
          if (selectedOption && selectedOption.value) {
            value = selectedOption.value;
          } else if (selectedOption && selectedOption.textContent) {
            // Проверяем, есть ли этот текст среди опций
            const options = Array.from(select.options).map(opt => opt.value);
            if (options.includes(selectedOption.textContent)) {
              value = selectedOption.textContent;
            } else {
              value = '';
            }
          } else {
            value = '';
          }
        }
        field.outerHTML = value;
      }
    });

    // Обновляем состояние с новым текстом
    updateText(zoneName, instanceId, element.innerHTML);
  };

  /**
   * Обработка нажатия клавиш в редактируемом div
   */
  const handleKeyDown = (e) => {
    // Обработка Ctrl+Z для отмены действий
    if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      document.execCommand('undo', false, null);
      return false;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Вставляем перенос строки
      document.execCommand('insertLineBreak');
      
      // Прокручиваем до каретки
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        // Находим родительский элемент с прокруткой
        const scrollParent = e.target;
        
        // Прокручиваем так, чтобы каретка была видна
        scrollParent.scrollTop = Math.max(0, rect.bottom - scrollParent.clientHeight + 20);
      }
      
      return false;
    }
  };

  const openEditModal = (group) => {
    setEditGroup(group);
    setEditGroupName(group.group_description);
    setEditGroupValue(group.group_value || '');
    setEditGroupReportValue(group.report_value || '');
    setIsEditModalOpen(true);
  };

  const openNewZoneModal = async () => {
    try {
      const res = await fetch(`${API_URL}/templates/?smart_template_name=` + templateId);
      const data = await res.json();
      let maxGroupId = 0;
      data.forEach(item => {
        if (item.group_id > maxGroupId) maxGroupId = item.group_id;
      });
      setGeneratedZoneSystemName(`${templateId}_zone_${maxGroupId + 1}`);
      setIsNewZoneModalOpen(true);
    } catch (e) {
      setGeneratedZoneSystemName(`${templateId}_zone_1`);
      setIsNewZoneModalOpen(true);
    }
  };

  const handleCreateNewZone = async () => {
    if (!newZoneDescription.trim()) {
      alert('Все поля обязательны!');
      return;
    }
    setIsSavingZone(true);
    try {
      // Получаем smart_template_description универсально
      let smartTemplateDescription = smartTemplateDescriptionFromState;
      if (!smartTemplateDescription) {
        // Делаем запрос к API по smart_template_name
        const res = await authFetch(`${API_URL}/templates/?smart_template_name=` + templateId);
        const data = await res.json();
        if (data.length > 0 && data[0].smart_template_description) {
          smartTemplateDescription = data[0].smart_template_description;
        } else {
          alert('Не удалось определить описание шаблона. Пожалуйста, перейдите в шаблон заново через главную страницу.');
          setIsSavingZone(false);
          return;
        }
      }
      // Получаем максимальный group_id
      const res = await authFetch(`${API_URL}/templates/?smart_template_name=` + templateId);
      const data = await res.json();
      let maxGroupId = 0;
      data.forEach(item => {
        if (item.group_id > maxGroupId) maxGroupId = item.group_id;
      });
      const newGroupId = maxGroupId + 1;
      // Формируем zone_name
      const zone_name = generatedZoneSystemName;
      // Формируем payload
      const payload = {
        smart_template_name: templateId,
        smart_template_description: smartTemplateDescription,
        zone_name,
        zone_description: newZoneDescription.trim(),
        parent_group_id: null,
        group_id: newGroupId,
        group_description: 'Добавить папку/описание',
        group_value: '',
        report_value: '',
        root: 1,
        normal: null
      };
      // Отправляем на сервер
      const createRes = await authFetch(`${API_URL}/create-group/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      const result = await createRes.json();
      if (result.success) {
        setIsNewZoneModalOpen(false);
        setNewZoneDescription('');
        await fetchData();
      } else {
        alert(result.error || 'Ошибка создания зоны');
      }
    } catch (err) {
      alert('Ошибка сети');
    } finally {
      setIsSavingZone(false);
    }
  };

  /**
   * Подготовка заключения - поиск и замена ключей в нередактируемых полях
   */
  const prepareConclusion = (zoneName, instanceId) => {
    console.log('prepareConclusion called with:', { zoneName, instanceId });

    // Находим все нередактируемые поля в заключении
    const conclusionElement = document.querySelector(`[data-zone="${zoneName}"][data-instance="${instanceId}"]`);
    console.log('conclusionElement:', conclusionElement);
    if (!conclusionElement) {
      console.log('conclusionElement not found');
      return;
    }

    const readonlyInputs = conclusionElement.querySelectorAll('input[readonly]');
    console.log('readonlyInputs found:', readonlyInputs.length);
    
    // Получаем текст всех зон (кроме заключения)
    const otherBlocks = blocks.filter(block => block.zone_name !== `${templateId}_conclusion`);
    console.log('otherBlocks found:', otherBlocks.length);

    const allZonesText = otherBlocks
      .map(block => 
        block.instances
          .map(inst => {
            // Получаем текст без HTML тегов
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = inst.text;
            // Заменяем <br> и </br> на пробелы перед извлечением текста
            tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, ' ');
            return tempDiv.textContent || tempDiv.innerText || '';
          })
          .join(' ')
      )
      .join(' ');
    
    console.log('allZonesText:', allZonesText);

    // Для каждого нередактируемого поля
    readonlyInputs.forEach(input => {
      console.log('Processing input with value:', input.value);
      const inputValue = input.value.trim();
      
      // Ищем префикс и ключ в формате %prefix-текст key-ключ%
      const prefixMatch = inputValue.match(/prefix-([^\s]+)/);
      const keyMatch = inputValue.match(/key-([^\s,]+)/);
      
      console.log('Prefix match:', prefixMatch);
      console.log('Key match:', keyMatch);

      if (!keyMatch) {
        console.log('No key found in:', inputValue);
        return; // Пропускаем поле, если в нем нет ключа
      }

      const searchKey = keyMatch[1]; // Берем группу после key-
      const searchPrefix = prefixMatch ? prefixMatch[1] : null; // Берем группу после prefix-
      
      console.log('Search key:', searchKey);
      console.log('Search prefix:', searchPrefix);
      if (!searchKey) return;

      // Сначала очищаем поле, оставляя только ключ и префикс
      const cleanedValue = `${searchPrefix ? `prefix-${searchPrefix} ` : ''}key-${searchKey}`;
      console.log('Cleaned value:', cleanedValue);
      input.value = cleanedValue;

      // Ищем все слова, ограниченные пробелами, знаками препинания или началом/концом строки
      const regex = new RegExp(
        `(?<=[\\s,.!?;:()\\[\\]{}\\n]|^)[^\\s,.!?;:()\\[\\]{}\\n]*${escapeRegExp(searchKey)}[^\\s,.!?;:()\\[\\]{}\\n]*(?=[\\s,.!?;:()\\[\\]{}\\n]|$)`,
        'g'
      );
      
      console.log('Search regex:', regex);
      const matchingWords = [];
      let match;
      while ((match = regex.exec(allZonesText)) !== null) {
        const word = match[0];
        console.log('Found word:', word);
        if (word !== searchKey) {
          // Проверяем префикс, если он указан
          if (searchPrefix) {
            // Приводим к нижнему регистру и схлапываем пробелы
            const normalizedText = allZonesText.toLowerCase().replace(/\s+/g, ' ');
            const normalizedPrefix = searchPrefix.toLowerCase();
            const normalizedWord = word.toLowerCase();
            const searchString = `${normalizedPrefix} ${normalizedWord}`;
            console.log('Пробуем найти:', `"${searchString}"`, 'в', normalizedText);
            if (normalizedText.includes(searchString)) {
              console.log('Prefix match found for word:', word);
              matchingWords.push(word);
            }
          } else {
            matchingWords.push(word);
          }
        }
      }

      console.log('Final matching words:', matchingWords);

      // Если нашли совпадения, обновляем значение поля
      if (matchingWords.length > 0) {
        // Убираем дубли
        const uniqueWords = Array.from(new Set(matchingWords));
        // Текст теперь подставляется перед prefix- и key-
        const newValue = `${uniqueWords.join(', ')} ${searchPrefix ? `prefix-${searchPrefix} ` : ''}key-${searchKey}`.trim();
        console.log('New value to set:', newValue);

        // Создаем новый input с обновленным значением
        const newInput = document.createElement('span');
        newInput.className = 'inline-field';
        newInput.contentEditable = 'false';
        newInput.innerHTML = `<input type=\"text\" class=\"inline-input\" data-field-id=\"${input.dataset.fieldId}\" value=\"${newValue}\" readonly />`;
        
        // Заменяем старый input новым
        input.parentElement.replaceWith(newInput);
      } else {
        console.log('No matching words found');
      }
    });

    // Сохраняем изменения в состоянии
    setBlocks(prev =>
      prev.map(block =>
        block.zone_name === zoneName
          ? {
              ...block,
              instances: block.instances.map(inst =>
                inst.id === instanceId
                  ? { ...inst, text: conclusionElement.innerHTML }
                  : inst
              )
            }
          : block
      )
    );
  };

  /**
   * Экранирование специальных символов в регулярном выражении
   */
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Вынесенная функция для обработки плейсхолдеров
  const processPlaceholders = (text) => {
    let processedText = renderTablePlaceholders(typeof text === 'string' ? text : '');

    // Синхронизированные плейсхолдеры [[sync:ID:Placeholder]]
    processedText = processedText.replace(
      /\[\[sync:([a-zA-Z0-9_\-]+):([^\]]+)\]\]/g,
      (match, syncId, placeholder) => {
        return `<span class="inline-field" contenteditable="false"><input type="text" class="inline-input sync-input" data-sync-id="${syncId}" placeholder="${placeholder}" /></span>`;
      }
    );
    // Обычные плейсхолдеры [[текст]]
    processedText = processedText.replace(
      /\[\[([^\]]+)\]\]/g,
      (match, placeholder) => {
        const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return `<span class="inline-field" contenteditable="false"><input type="text" class="inline-input" data-field-id="${fieldId}" placeholder="${placeholder}" /></span>`;
      }
    );
    // Синхронизированные выпадающие списки ((sync:ID:текст)){{значения}}
    processedText = processedText.replace(
      /\(\(sync:([a-zA-Z0-9_\-]+):([^)]+)\)\)\{\{([^}]+)\}\}/g,
      (match, syncId, name, values) => {
        const options = values.split(';').map(v => v.trim());
        // Если name совпадает с одной из опций, делаем её выбранной по умолчанию
        if (options.includes(name)) {
          return `<span class="inline-field" contenteditable="false"><select class="inline-select sync-select" data-sync-id="${syncId}">
            ${options.map(opt => `<option value="${opt}"${opt === name ? ' selected' : ''}>${opt}</option>`).join('')}
          </select></span>`;
        } else {
          // Если не совпадает — добавляем как placeholder
          return `<span class="inline-field" contenteditable="false"><select class="inline-select sync-select" data-sync-id="${syncId}">
            <option value="" selected>${name}</option>
            ${options.map(opt => `<option value="${opt}">${opt}</option>`).join('')}
          </select></span>`;
        }
      }
    );
    // Обычные выпадающие списки ((текст)){{значения}}
    processedText = processedText.replace(
      /\(\(([^)]+)\)\)\{\{([^}]+)\}\}/g,
      (match, name, values) => {
        const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const options = values.split(';').map(v => v.trim());
        // Если name совпадает с одной из опций, делаем её выбранной по умолчанию и не добавляем как отдельный option
        if (options.includes(name)) {
          return `<span class="inline-field" contenteditable="false"><select class="inline-select" data-field-id="${fieldId}">
            ${options.map(opt => `<option value="${opt}"${opt === name ? ' selected' : ''}>${opt}</option>`).join('')}
          </select></span>`;
        } else {
          // Если не совпадает — добавляем как placeholder
          return `<span class="inline-field" contenteditable="false"><select class="inline-select" data-field-id="${fieldId}">
            <option value="" selected>${name}</option>
            ${options.map(opt => `<option value="${opt}">${opt}</option>`).join('')}
          </select></span>`;
        }
      }
    );
    return processedText;
  };

  const { isNormaPrefilling, startNormaPrefill } = useNormaPrefill({
    apiUrl: API_URL,
    blocks,
    processPlaceholders,
    setBlocks,
    templateId,
  });

  /**
   * Перемещение зоны вверх - уменьшение zone_index
   */
  const handleMoveZoneUp = async (zoneIndex) => {
    const block = blocks[zoneIndex];
    if (!block || block.zone_name === `${templateId}_conclusion`) {
      return; // Не перемещаем заключение
    }

    try {
      const response = await authFetch(`${API_URL}/decrease-zone-index/${block.zone_name}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();
      console.log(result);
      
      if (result.success) {
        // Обновляем данные с сервера
        await fetchData();
      } else {
        alert(result.error || 'Ошибка при перемещении зоны');
      }
    } catch (error) {
      console.error('Ошибка при перемещении зоны:', error);
      alert('Ошибка сети при перемещении зоны');
    }
  };

  /**
   * Перемещение зоны вниз - увеличение zone_index
   */
  const handleMoveZoneDown = async (zoneIndex) => {
    const block = blocks[zoneIndex];
    if (!block || block.zone_name === `${templateId}_conclusion`) {
      return; // Не перемещаем заключение
    }

    try {
      const response = await authFetch(`${API_URL}/increase-zone-index/${block.zone_name}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();
      console.log(result);
      
      if (result.success) {
        // Обновляем данные с сервера
        await fetchData();
      } else {
        alert(result.error || 'Ошибка при перемещении зоны');
      }
    } catch (error) {
      console.error('Ошибка при перемещении зоны:', error);
      alert('Ошибка сети при перемещении зоны');
    }
  };

  useEffect(() => {
    const handleSyncInput = (e) => {
      if (e.target.classList.contains('sync-input')) {
        const syncId = e.target.getAttribute('data-sync-id');
        const value = e.target.value;
        document.querySelectorAll(`.sync-input[data-sync-id="${syncId}"]`).forEach(input => {
          if (input !== e.target) {
            input.value = value;
          }
        });
      }
    };
    
    const handleSyncSelect = (e) => {
      if (e.target.classList.contains('sync-select')) {
        const syncId = e.target.getAttribute('data-sync-id');
        const value = e.target.value;
        document.querySelectorAll(`.sync-select[data-sync-id="${syncId}"]`).forEach(select => {
          if (select !== e.target) {
            select.value = value;
          }
        });
      }
    };
    
    document.addEventListener('input', handleSyncInput);
    document.addEventListener('change', handleSyncSelect);
    return () => {
      document.removeEventListener('input', handleSyncInput);
      document.removeEventListener('change', handleSyncSelect);
    };
  }, []);

  // Обновляем useEffect для восстановления значений плейсхолдеров
  useEffect(() => {
    const { zoneName, instanceId, pos } = caretPositionRef.current || {};
    if (zoneName && instanceId && pos !== null) {
      const element = document.querySelector(`[data-zone="${zoneName}"][data-instance="${instanceId}"]`);
      if (element) {
        restoreCaretPosition(element, pos);
        restorePlaceholderValues(zoneName, instanceId, element);
      }
      caretPositionRef.current = { zoneName: null, instanceId: null, pos: null };
    }
  }, [blocks]);

  // ================ Компоненты ================
  /**
   * Компонент элемента группы
   */
  const GroupItem = ({
    group,
    zoneName,
    instanceId,
    depth = 0
  }) => {
    const paddingLeft = 20 * depth;
    const hasChildren = expandedGroups[group.group_id]?.length > 0;

    return (
      <div>
        <div className="tool-row" style={{ paddingLeft }}>
          <button
          className={`tool-item
            ${!group.group_value ? 'no-value' : ''}
            ${group.root === 1 ? 'is-root' : ''}
          `}
            onClick={
              group.group_value
                ? () => handleInsertText(zoneName, instanceId, group.group_id)
                : () => toggleChildGroups(group.group_id)
            }
            title={group.group_value ? "Добавить в поле" : "Показать подгруппы"}
          >
            {group.group_description}
          </button>

          <div className="tool-icons">
            {!group.group_value && (
              <>
                <button 
                  className="icon-btn" 
                  title="Поддиректория" 
                  onClick={() => {
                    setModalParentGroup(group);
                    setIsModalOpen(true);
                  }}
                >
                  📁
                </button>
                <button 
                  className="icon-btn" 
                  title="Добавить значение" 
                  onClick={() => {
                    setModalPlusGroup(group);
                    setIsPlusModalOpen(true);
                  }}
                >
                  ➕
                </button>
              </>
            )}
            {group.root !== 1 && (
                  <button
                    className="icon-btn"
                    title="Удалить"
                    onClick={async () => {
                      if (window.confirm('Точно удалить группу?')) {
                        try {
                          const res = await authFetch(
                            `${API_URL}/delete-group/${group.group_id}/`,
                            {
                              method: 'DELETE',
                            }
                          );
                          const result = await res.json();
                          if (result.success) {
                            alert('Группа удалена');
                            await fetchData();
                        setExpandedGroups({});
                          } else {
                            alert(result.error || 'Ошибка удаления');
                          }
                        } catch (err) {
                          console.error('Ошибка удаления:', err);
                          alert('Ошибка при удалении');
                        }
                      }
                    }}
                  >
                    🗑
                  </button>
                )}
            {group.root !== 1 && (
                <button
                  className="icon-btn"
                  title="Редактировать"
                onClick={() => openEditModal(group)}
              >
                ✏️
              </button>
               )}
          </div>
        </div>

        {hasChildren && (
          <div className="nested-group-list">
            {expandedGroups[group.group_id].map((child) => (
              <GroupItem
                key={child.group_id}
                group={child}
                zoneName={zoneName}
                instanceId={instanceId}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Обновляем PatientInfoBlock для сохранения значений при изменении даты
  const handleDateChange = (newDate) => {
    // Сохраняем значения перед изменением даты
    saveAllPlaceholderValues();
    
    setStudyDate(newDate);
    
    // Восстанавливаем значения после обновления состояния
    setTimeout(() => {
      restoreAllPlaceholderValues();
    }, 0);
  };

  // --- Undo/Redo ---
  const handleUndo = (zoneName, instanceId) => {
    setBlocks(prev => handleUndoUtil(prev, zoneName, instanceId));
  };

  const handleRedo = (zoneName, instanceId) => {
    setBlocks(prev => handleRedoUtil(prev, zoneName, instanceId));
  };

  const formConclusionForWordExport = () => {
    const conclusionBlock = blocks.find(
      block => block.zone_name === `${templateId}_conclusion`
    );

    conclusionBlock?.instances.forEach(instance => {
      handleFormConclusion(conclusionBlock.zone_name, instance.id);
    });
  };

  const {
    customTemplateFileName,
    fileInputRef,
    handleTemplateFileChange,
    hasCustomTemplate,
    isExporting,
    startWordExport,
  } = useWordExport(
    {
      blocks,
      images,
      recommendationsText,
      patientName,
      patientBirthdate,
      patientGender,
      studyNumber,
      studyDate,
      studyType,
      contrastType,
      studyNote,
      complaints,
    },
    {
      applyAllPlaceholders,
      formConclusion: formConclusionForWordExport,
    }
  );

  // ================ Рендеринг ================
  return (
    <div className="template-container">
      {/* Панель управления */}
      <div style={{ marginBottom: '0em', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="main-action-btn" onClick={() => navigate('/')}>⬅️ На главную</button>
          <button className="main-action-btn danger" onClick={clearDraft}>🧹 Очистить черновик</button>
          <WordExportButton
            className="main-action-btn word"
            disabled={isExporting}
            onClick={startWordExport}
          >
            📄 Выгрузить в Word
          </WordExportButton>
          <button className="main-action-btn" onClick={openNewZoneModal}>Новая зона исследования</button>
          <button
            className="main-action-btn norma"
            onClick={startNormaPrefill}
            disabled={isNormaPrefilling}
          >
            Норма
          </button>
        </div>
        <div className="word-template-upload-row">
          <AiPromptLibrary isAvailable={aiAccess} />
          <div className="word-template-upload-control">
            <button
              className="main-action-btn upload-template-btn"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              Загрузить шаблон
            </button>
            {hasCustomTemplate && (
              <span
                className="word-template-upload-status"
                title={customTemplateFileName || 'Шаблон загружен'}
              >
                {customTemplateFileName || 'Шаблон загружен'}
              </span>
            )}
            <input
              type="file"
              accept=".docx"
              aria-label="Файл пользовательского Word-шаблона"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleTemplateFileChange}
            />
          </div>
        </div>
      </div>
      <h1 className="template-title">{templateDescription}</h1>

      {/* Основной контент */}
      {/* Блок "Данные по пациенту" */}
      <PatientInfoBlock
        patientName={patientName}
        setPatientName={setPatientName}
        patientBirthdate={patientBirthdate}
        setPatientBirthdate={setPatientBirthdate}
        patientGender={patientGender}
        setPatientGender={setPatientGender}
        studyNumber={studyNumber}
        setStudyNumber={setStudyNumber}
        studyDate={studyDate}
        setStudyDate={handleDateChange}
        studyType={studyType}
        setStudyType={setStudyType}
        contrastType={contrastType}
        setContrastType={setContrastType}
        studyNote={studyNote}
        setStudyNote={setStudyNote}
        complaints={complaints}
        setComplaints={setComplaints}
        templateId={templateId}
        alias={getTemplateAlias(templateId)}
        aiAccess={aiAccess}
      />

      {blocks.map((block, blockIdx) =>
        block.instances.map((instance) => {
          const key = `${block.zone_name}-${instance.id}`;
          const isConclusion = block.zone_name === `${templateId}_conclusion`;
          const lastZoneIdx = blocks.length - 2;
          const isSpecialBlock = block.zone_name === `${templateId}_conclusion` || block.zone_name === `${templateId}_recommendations` || block.zone_name === undefined;

          return (
            <div key={instance.id} className="template-block" style={{ paddingLeft: 10 }}>
              {/* Для позиционирования кнопки-урны */}
              <style>{`.template-block { position: relative; }`}</style>
              {/* Стрелки перемещения */}
              {!isConclusion && (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginRight: 0 }}>
                  <button
                    className="move-arrow-btn"
                    onClick={() => handleMoveZoneUp(blockIdx)}
                    disabled={blockIdx === 0}
                    title="Переместить вверх"
                    style={{ marginBottom: 2 }}
                  >
                    ▲
                  </button>
                  <button
                    className="move-arrow-btn"
                    onClick={() => handleMoveZoneDown(blockIdx)}
                    disabled={blockIdx === lastZoneIdx}
                    title="Переместить вниз"
                  >
                    ▼
                  </button>
                </div>
              )}
              {isConclusion && (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginRight: 0 }}>
                  <button
                    className="move-arrow-btn"
                    disabled
                    style={{ marginBottom: 2 }}
                    title="Переместить вверх"
                  >
                    ▲
                  </button>
                  <button
                    className="move-arrow-btn"
                    disabled
                    title="Переместить вниз"
                  >
                    ▼
                  </button>
                </div>
              )}
              {/* Описание зоны */}
              <div className="zone-description" style={{ flexBasis: '10%', width: 140, minWidth: 140, maxWidth: 140, fontSize: '0.9rem' }}>
                {block.zone_description}
              </div>
              {/* Кнопка удаления зоны */}
              {!isSpecialBlock && instance.id === block.instances[0].id && activeZoneName === block.zone_name && (
                <>
                  <button
                    type="button"
                    style={{
                      position: 'absolute',
                      left: 8,
                      bottom: 8,
                      width: 18,
                      height: 18,
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 11,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                    }}
                    title="Удалить зону исследования"
                    onClick={async () => {
                      // Проверка: zone_name встречается только 1 раз
                      try {
                        const res = await authFetch(`${API_URL}/templates/?zone_name=${block.zone_name}`);
                        const data = await res.json();
                        console.log('Найдено записей с этим zone_name:', data.length, data);
                        if (!Array.isArray(data) || data.length !== 1) {
                          alert('Зону можно удалить только если она пустая и не содержит вложенных папок или значений.');
                          return;
                        }
                      } catch (err) {
                        alert('Ошибка проверки зоны перед удалением');
                        return;
                      }
                      if (window.confirm('Вы действительно хотите удалить зону исследования?')) {
                        try {
                          const res = await authFetch(`${API_URL}/delete-zone/${block.zone_name}/`, {
                            method: 'DELETE',
                          });
                          const result = await res.json();
                          if (result.success) {
                            alert('Зона удалена');
                            await fetchData();
                          } else {
                            alert(result.error || 'Ошибка удаления зоны');
                          }
                        } catch (err) {
                          alert('Ошибка при удалении зоны');
                        }
                      }
                    }}
                  >
                    🗑
                  </button>
                  <button
                    type="button"
                    style={{
                      position: 'absolute',
                      left: 28,
                      bottom: 8,
                      width: 18,
                      height: 18,
                      background: '#adb5bd',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 11,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                    }}
                    title="Редактировать описание зоны"
                    onClick={() => {
                      setEditZoneDescription(block.zone_description);
                      setEditZoneName(block.zone_name);
                      setIsEditZoneModalOpen(true);
                    }}
                  >
                    ✏️
                  </button>
                </>
              )}
              {/* Текстовый блок */}
              <div className="block-text">
                <div
                  className="editable-content"
                  contentEditable
                  role="textbox"
                  aria-label={`Текст зоны: ${block.zone_description}`}
                  aria-multiline="true"
                  dangerouslySetInnerHTML={{ __html: instance.text }}
                  onInput={(e) => handleContentChange(block.zone_name, instance.id, e)}
                  onClick={handlePlaceholderClick}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setActiveZoneName(block.zone_name);
                    // Открываем библиотеку заключений при активации зоны (только для обычных зон)
                    if (!isConclusion) {
                      const zoneKey = `${block.zone_name}-${instance.id}`;
                      // Открываем библиотеку только если она еще не открыта для этой зоны
                      if (openZone !== zoneKey) {
                        setOpenZone(zoneKey);
                      }
                    } else {
                      // При активации зоны заключения закрываем все раскрытые библиотеки
                      if (openZone !== null) {
                        setOpenZone(null);
                      }
                    }
                  }}
                  suppressContentEditableWarning={true}
                  data-zone={block.zone_name}
                  data-instance={instance.id}
                  style={{ minHeight: 24 }}
                />
                {!isConclusion && activeZoneName === block.zone_name && (
                <div className="block-controls">
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => removeInstance(block.zone_name, instance.id)}
                    disabled={block.instances.length === 1}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0
                    }}
                  >
                    🗑
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUndo(block.zone_name, instance.id)}
                    disabled={instance.historyIndex === 0}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0
                    }}
                    title="Отменить"
                  >
                    ↶
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRedo(block.zone_name, instance.id)}
                    disabled={instance.historyIndex === instance.history.length - 1}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0
                    }}
                    title="Вернуть"
                  >
                    ↷
                  </button>
                  <button
                    type="button"
                    onClick={() => clearText(block.zone_name, instance.id)}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0
                    }}
                  >
                    🧹
                  </button>
                  <button
                    type="button"
                    onClick={() => copyInstance(block.zone_name)}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      padding: 0
                    }}
                  >
                    +
                  </button>
                  <button 
                    type="button"
                    onClick={() => applyPlaceholders(block.zone_name, instance.id)}
                    style={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      maxWidth: 32,
                      maxHeight: 32,
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0
                    }}
                  >
                    ✓
                  </button>
                </div>
                )}
                {isConclusion && (
                  <div className="block-controls">
                    <button
                      onClick={() => handleUndo(block.zone_name, instance.id)}
                      disabled={instance.historyIndex === 0}
                      style={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        maxWidth: 32,
                        maxHeight: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18,
                        padding: 0
                      }}
                      title="Отменить"
                    >
                      ↶
                    </button>
                    <button
                      onClick={() => handleRedo(block.zone_name, instance.id)}
                      disabled={instance.historyIndex === instance.history.length - 1}
                      style={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        maxWidth: 32,
                        maxHeight: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18,
                        padding: 0
                      }}
                      title="Вернуть"
                    >
                      ↷
                    </button>
                    <button
                      onClick={() => clearText(block.zone_name, instance.id)}
                      style={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        maxWidth: 32,
                        maxHeight: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18,
                        padding: 0
                      }}
                    >
                      🧹
                    </button>
                    <button className="main-action-btn danger"
                      onClick={() => applyAllPlaceholders()}
                      style={{              
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '0px'
                      }}
                      title="Обязательно перед выгрузкой в Word! Перевести все плэйсхолдеры ВСЕХ БЛОКОВ кроме Заключения в текст."
                    >
                      🚨Применить все
                    </button>
                    <button className="main-action-btn conclusion"
                      onClick={() => handleFormConclusion(block.zone_name, instance.id)}
                      style={{
                        color: 'black',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: 0,
                      }}
                      title="Обязательно перед выгрузкой в Word! Перевести все плэйсхолдеры блока ЗАКЛЮЧЕНИЕ в текст."
                    >
                      🚨Сформировать
                    </button>
                    {aiAccess && (
                      <button className="main-action-btn"
                        onClick={async () => {
                          try {
                            // Собираем текстовые значения из всех зон исследования
                            const researchZones = blocks.filter(block => 
                              !block.zone_name.endsWith('_conclusion') && 
                              !block.zone_name.endsWith('_recommendations')
                            );
                            
                            // Подготавливаем данные для отправки на бэкенд
                            const researchZonesData = researchZones.map(block => ({
                              zone_name: block.zone_name,
                              instances: block.instances
                            }));
                            
                            // Вызываем новый эндпоинт
                            const response = await authFetch(`${API_URL}/generate-conclusion/`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                complaints: complaints,
                                research_zones: researchZonesData,
                                patient_birthdate: patientBirthdate,
                                patient_gender: patientGender,
                                study_type: studyType
                              })
                            });
                            
                            if (response.ok) {
                              const data = await response.json();
                              if (data.success && data.conclusion_text) {
                                // Добавляем новый текст к существующему в заключении, пропустив абзац
                                setBlocks(prev =>
                                  prev.map(block =>
                                    block.zone_name === `${templateId}_conclusion`
                                      ? {
                                          ...block,
                                          instances: block.instances.map(inst => ({
                                            ...inst,
                                            text: inst.text ? inst.text + '<br><br>' + data.conclusion_text : data.conclusion_text
                                          }))
                                        }
                                      : block
                                  )
                                );
                              }
                            } else {
                              console.error('Ошибка при генерации заключения:', response.statusText);
                            }
                          } catch (error) {
                            console.error('Ошибка при вызове API:', error);
                          }
                        }}
                        style={{
                          color: 'white',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginLeft: 0,
                          marginRight: 0,
                        }}
                        title="Подготовить промт для AI генерации заключения"
                      >
                        🤖 Подготовить промт
                      </button>
                    )}
                    {/* Заглушка для отступа для пользователей, у которых нет доступа к AI */}
                    <button 
                      style={{
                        marginRight: 454,
                        visibility: 'hidden',
                      }}
                    ></button>
                  </div>
                )}
              </div>

              {/* Библиотека заключений */}
              {!isConclusion && (
              <div className="block-tools">
                  <button 
                    className="tool-button" 
                    onClick={() => toggleZone(block.zone_name, instance.id)}
                  >
                    Библиотека заключений
                  </button>
                {openZone === key && (
                  <div
                    className={`tool-list ${openZone === key ? 'show' : ''}`}
                    ref={(el) => {
                      if (openZone === key) {
                        el?.scrollTo?.(0, 0);
                      }
                    }}
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'auto',
                    }}
                  >
                    {block.groups
                      .sort((a, b) => {
                        // Сначала проверяем root = 1
                        if (a.root === 1 && b.root !== 1) return -1;
                        if (a.root !== 1 && b.root === 1) return 1;
                        // Затем группируем по наличию group_value
                        if (a.group_value && !b.group_value) return -1;
                        if (!a.group_value && b.group_value) return 1;
                        // Затем сортируем по group_id внутри каждой группы
                        return a.group_id - b.group_id;
                      })
                      .map((group) => (
                      <GroupItem
                        key={group.group_id}
                        group={group}
                        zoneName={block.zone_name}
                        instanceId={instance.id}
                      />
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Выпадающий список для выбора значения */}
              {!isConclusion && activePopup && (
                <div
                  ref={popupRef}
                  className="popup-select"
                  style={{
                    position: 'absolute',
                    top: `${activePopup.position.top}px`,
                    left: `${activePopup.position.left}px`,
                    zIndex: 1000,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    padding: '5px 0'
                  }}
                >
                  {activePopup.values.map((value, index) => (
                    <div
                      key={index}
                      onClick={() => handleValueSelect(block.zone_name, instance.id, activePopup.placeholder, value)}
                      style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        hover: {
                          backgroundColor: '#f0f0f0'
                        }
                      }}
                    >
                      {value}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Блок "Рекомендации" */}
      <RecommendationsBlock
        recommendationsText={recommendationsText}
        setRecommendationsText={setRecommendationsText}
        exportToWord={startWordExport}
        isExportDisabled={isExporting}
      />

      {/* Блок "Снимки" */}
      <PicturesBlock images={images} setImages={setImages} />

      {/* Модальные окна */}
      {/* Модальное окно добавления папки */}
      <CreateGroupModal
        isOpen={isModalOpen}
        parentGroup={modalParentGroup}
        newGroupName={newGroupName}
        onChangeGroupName={setNewGroupName}
        onSave={handleCreateGroup}
        onCancel={() => setIsModalOpen(false)}
      />

      {/* Модальное окно добавления значения */}
      <CreateValueModal
        isOpen={isPlusModalOpen}
        parentGroup={modalPlusGroup}
        newGroupName={newPlusGroupName}
        onChangeGroupName={setNewPlusGroupName}
        newValue={newPlusValue}
        onChangeValue={setNewPlusValue}
        newReportValue={newPlusReportValue}
        onChangeReportValue={setNewPlusReportValue}
        onSave={handleCreatePlusGroup}
        onCancel={() => setIsPlusModalOpen(false)}
        isNormal={isPlusNormal}
        onChangeIsNormal={setIsPlusNormal}
      />

      {/* Модальное окно редактирования */}
      <EditGroupModal
        isOpen={isEditModalOpen}
        group={editGroup}
        groupName={editGroupName}
        onChangeGroupName={setEditGroupName}
        groupValue={editGroupValue}
        onChangeGroupValue={setEditGroupValue}
        groupReportValue={editGroupReportValue}
        onChangeGroupReportValue={setEditGroupReportValue}
        onSave={async () => {
          try {
            const normal = isEditNormal ? 1 : 0;
            const payload = {
              group_description: editGroupName,
              group_value: editGroupValue
            };
            if (editGroup.group_value !== null && editGroup.group_value !== '') {
              payload.report_value = editGroupReportValue;
              payload.normal = normal;
            }
            const res = await authFetch(`${API_URL}/update-group/${editGroup.group_id}/`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (result.success) {
              setIsEditModalOpen(false);
              setEditGroup(null);
              setEditGroupName('');
              setEditGroupValue('');
              setEditGroupReportValue('');
              await fetchData();
              setExpandedGroups({});
            } else {
              alert(result.error || 'Ошибка при обновлении');
            }
          } catch (err) {
            console.error('Ошибка сети:', err);
            alert('Ошибка сети');
          }
        }}
        onCancel={() => setIsEditModalOpen(false)}
        isNormal={isEditNormal}
        onChangeIsNormal={setIsEditNormal}
      />

      {/* Модальное окно создания новой зоны */}
      <NewZoneModal
        isOpen={isNewZoneModalOpen}
        isSaving={isSavingZone}
        newZoneDescription={newZoneDescription}
        onChangeDescription={setNewZoneDescription}
        onSave={handleCreateNewZone}
        onCancel={() => setIsNewZoneModalOpen(false)}
      />

      {/* Модальное окно редактирования описания зоны */}
      <EditZoneModal
        isOpen={isEditZoneModalOpen}
        editZoneDescription={editZoneDescription}
        onChangeDescription={setEditZoneDescription}
        onSave={async () => {
          if (!editZoneDescription.trim()) {
            alert('Описание не может быть пустым');
            return;
          }
          try {
            const res = await authFetch(`${API_URL}/update-zone-description/${editZoneName}/`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ zone_description: editZoneDescription })
            });
            const result = await res.json();
            if (result.success) {
              setIsEditZoneModalOpen(false);
              setEditZoneDescription('');
              setEditZoneName('');
              await fetchData();
            } else {
              alert(result.error || 'Ошибка при обновлении');
            }
          } catch (err) {
            alert('Ошибка сети');
          }
        }}
        onCancel={() => setIsEditZoneModalOpen(false)}
      />

      <WordExportProgressOverlay isOpen={isExporting} />
    </div>
  );
};

export default TemplatePage;
